import type { Envergure } from './brand';

/**
 * Brand Strategy validation status.
 * - draft     : strategy exists but never validated (or was cleared)
 * - validated : user explicitly validated the current content; sourceHash snapshot taken
 * - stale     : content changed after validation (hash mismatch); must be re-validated
 *
 * Initial state when no strategy exists: no meta object at all.
 */
export type BrandStrategyStatus = 'draft' | 'validated' | 'stale';

/**
 * GEO Strategy validation status.
 * - none      : no GEO strategy generated/saved yet
 * - pending   : generated or drafted, awaiting user review & validation
 * - validated : user validated the current content
 * - stale     : Brand Strategy changed after GEO validation → must be re-inferred or re-validated
 */
export type GeoStrategyStatus = 'none' | 'pending' | 'validated' | 'stale';

/**
 * Metadata tracked alongside each strategy object.
 *
 * `sourceHash` is the deterministic hash of the Brand Strategy snapshot at the time of
 * validation (for BrandStrategyMeta) or at the time of GEO generation/validation (for
 * GeoStrategyMeta). When the current Brand Strategy hash diverges, consumers flip the
 * status to `stale`.
 */
export interface BrandStrategyMeta {
  status: BrandStrategyStatus;
  validatedAt?: string;
  /** sha256 of the Brand Strategy at validation time. */
  sourceHash?: string;
}

export interface GeoStrategyMeta {
  status: GeoStrategyStatus;
  /** When the agent or user last drafted this GEO strategy. */
  generatedAt?: string;
  /** When the user validated the current content. */
  validatedAt?: string;
  /** Hash of the Brand Strategy snapshot the GEO strategy was derived from. */
  sourceHash?: string;
  /**
   * Number of agent-generate (auto + "Régénérer") runs against this brand.
   * Capped server-side to bound LLM cost (see GEO_GENERATE_MAX). Persisted so the
   * limit survives reloads.
   */
  generateCount?: number;
}

/** Max number of GEO Strategy agent-generations per brand (initial + regenerations). */
export const GEO_GENERATE_MAX = 4;

/**
 * GEO (Generative Engine Optimization) Strategy.
 *
 * This is the action plan that agents consume to decide WHAT queries to monitor, WHICH
 * authority sources to watch, WHICH content pillars to build, etc. It is derived from —
 * but distinct from — Brand Strategy (identity/positioning). GEO Strategy is usually
 * pre-filled by the GEO Strategist agent (Lot 4) and then edited + validated by a human.
 *
 * All fields are optional: agents degrade gracefully when GEO Strategy is absent.
 */
export interface GeoStrategy {
  /**
   * Target geographic footprint the brand aims to reach (distinct from current
   * `brandStrategy.envergure`). Drives the geographic spread of monitored queries
   * in Brand Referral.
   */
  targetEnvergure?: Envergure;

  /**
   * Concrete AI-engine queries the brand wants to be cited on. Agents add these to
   * the Brand Perception query matrix on top of strategy-derived queries.
   */
  targetQueries?: string[];

  /**
   * Strategic positioning angle in 1-3 sentences — the core "why us, not them" story
   * the brand wants AI engines to pick up. Consumed as prompt context by Brand
   * Perception & Earned Media for scoring calibration.
   */
  strategicAngle?: string;

  /**
   * Named authority domains/outlets the brand wants to appear in. Earned Media uses
   * these as a PRIORITY allowlist on top of its empirical discovery.
   */
  targetAuthoritySources?: string[];

  /**
   * Macro content themes the brand commits to owning over the next 6-12 months.
   * Drives Content Quality triage and News Watch topic filters.
   */
  contentPillars?: string[];

  /**
   * Free-text note on the query-generation style: long-tail vs head, question vs
   * comparison, language mix, etc. Guides agent prompt construction.
   */
  queryStrategy?: string;

  /**
   * Named competitors the brand wants to outrank/outcite. Agents prioritize these
   * in comparative queries over the generic Brand Strategy competitor list.
   */
  competitiveFocus?: string[];
}

/**
 * Character / item limits for GEO Strategy. Enforced on both frontend (live counter)
 * and backend (validation).
 */
export const GEO_STRATEGY_LIMITS = {
  strategicAngle: 600,
  queryStrategy: 400,
  targetQueries: { maxItems: 30, maxCharsPerItem: 200 },
  targetAuthoritySources: { maxItems: 20, maxCharsPerItem: 200 },
  contentPillars: { maxItems: 10, maxCharsPerItem: 120 },
  competitiveFocus: { maxItems: 15, maxCharsPerItem: 120 },
} as const;
